import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import clsx from 'clsx';
import { listAdminKpiApi } from '@/lib/kpi-api';
import { listDepartmentsApi } from '@/lib/employees-api';
import {
  DEFAULT_KPI_FILTER,
  KPI_GRANULARITY_OPTIONS,
  KPI_PAGE_SIZE_OPTIONS,
  KPI_SORT_OPTIONS,
  buildKpiQuery,
  formatOnlineDuration,
  normalizeKpiPageSize,
  type KpiFilterState,
} from '@/lib/kpi-filters';
import type { KpiGranularity, KpiSortBy } from '@/lib/kpi-api';
import { Card } from '@/components/ui/card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/empty-state';

const INPUT =
  'rounded-md border border-slate-300 px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const SECONDARY_BTN =
  'inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs text-slate-600 transition hover:bg-slate-50 disabled:opacity-60';
const CHIP = 'rounded-md px-3 py-1 text-xs font-medium transition';

/**
 * Issue #346 — 数据总览「KPI 考核」版块（ADMIN+）。
 * 日/周/月切换 + 关键词 / 部门筛选 + 分项积分 + 总积分排序 + 后端真实分页。
 * 数据全部来自 GET /admin/kpi（口径同 KpiPointsService），绝不前端假分页 / 硬编码。
 */
export function KpiWorkspace() {
  const [filter, setFilter] = useState<KpiFilterState>(DEFAULT_KPI_FILTER);
  const [pendingKeyword, setPendingKeyword] = useState('');

  const params = buildKpiQuery(filter);
  const query = useQuery({
    queryKey: ['admin', 'kpi', params],
    queryFn: () => listAdminKpiApi(params),
    placeholderData: keepPreviousData,
  });
  const deptQuery = useQuery({
    queryKey: ['admin', 'departments'],
    queryFn: listDepartmentsApi,
    staleTime: 60 * 1000,
  });

  const patch = (next: Partial<KpiFilterState>) =>
    // 任意筛选/排序/页大小变更回到第 1 页；翻页显式带 page。
    setFilter((prev) => ({ ...prev, ...next, page: next.page ?? 1 }));

  const rows = query.data?.items ?? [];
  const pagination = query.data?.pagination;
  const total = pagination?.total ?? rows.length;
  const currentPage = pagination?.page ?? filter.page;
  const totalPages = pagination?.totalPages ?? 1;
  const windowLabel = query.data?.window?.label ?? '';
  const depts = deptQuery.data ?? [];

  const toggleSort = (key: KpiSortBy) => {
    if (filter.sortBy === key) {
      patch({ sortOrder: filter.sortOrder === 'asc' ? 'desc' : 'asc' });
    } else {
      // 姓名默认升序，积分类默认降序（大在前）。
      patch({ sortBy: key, sortOrder: key === 'name' ? 'asc' : 'desc' });
    }
  };

  const sortArrow = (key: KpiSortBy) =>
    filter.sortBy === key ? (filter.sortOrder === 'asc' ? ' ↑' : ' ↓') : '';

  const dateHint = useMemo(() => {
    if (filter.granularity === 'day') return '选择某一天';
    if (filter.granularity === 'week') return '选择周内任意一天';
    return '选择月内任意一天';
  }, [filter.granularity]);

  return (
    <Card
      title="KPI 考核"
      action={
        <div className="flex items-center gap-1" data-testid="kpi-granularity">
          {KPI_GRANULARITY_OPTIONS.map((g) => (
            <button
              key={g.value}
              type="button"
              className={clsx(
                CHIP,
                filter.granularity === g.value
                  ? 'bg-brand-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200',
              )}
              onClick={() => patch({ granularity: g.value as KpiGranularity })}
              data-testid={`kpi-granularity-${g.value}`}
            >
              {g.label}
            </button>
          ))}
        </div>
      }
    >
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>关键词（姓名 / 工号 / 岗位 / 部门）</span>
          <div className="flex items-center gap-1">
            <input
              className={clsx(INPUT, 'w-52')}
              value={pendingKeyword}
              placeholder="搜索员工…"
              onChange={(e) => setPendingKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') patch({ keyword: pendingKeyword });
              }}
              data-testid="kpi-keyword"
            />
            <button
              type="button"
              className={SECONDARY_BTN}
              onClick={() => patch({ keyword: pendingKeyword })}
            >
              <Search className="h-3.5 w-3.5" />
              查询
            </button>
          </div>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>部门</span>
          <select
            className={INPUT}
            value={filter.deptId}
            onChange={(e) => patch({ deptId: e.target.value })}
            data-testid="kpi-dept"
          >
            <option value="">全部部门</option>
            {depts.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>日期（{dateHint}）</span>
          <input
            type="date"
            className={INPUT}
            value={filter.date}
            onChange={(e) => patch({ date: e.target.value })}
            data-testid="kpi-date"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-slate-500">
          <span>排序</span>
          <select
            className={INPUT}
            value={filter.sortBy}
            onChange={(e) =>
              patch({
                sortBy: e.target.value as KpiSortBy,
                sortOrder: (e.target.value as KpiSortBy) === 'name' ? 'asc' : 'desc',
              })
            }
            data-testid="kpi-sort"
          >
            {KPI_SORT_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={SECONDARY_BTN}
          onClick={() => patch({ sortOrder: filter.sortOrder === 'asc' ? 'desc' : 'asc' })}
          data-testid="kpi-sort-order"
        >
          {filter.sortOrder === 'asc' ? '升序 ↑' : '降序 ↓'}
        </button>
        {windowLabel ? (
          <span className="ml-auto self-center text-xs text-slate-400" data-testid="kpi-window-label">
            统计区间：{windowLabel}
          </span>
        ) : null}
      </div>

      {query.isLoading ? (
        <LoadingState label="正在统计员工 KPI…" />
      ) : query.isError ? (
        <ErrorState
          title="KPI 接口加载失败"
          description={query.error instanceof Error ? query.error.message : '请稍后重试'}
          retry={() => void query.refetch()}
        />
      ) : total === 0 ? (
        <EmptyState
          title={filter.keyword || filter.deptId ? '没有匹配的员工' : '暂无 KPI 数据'}
          description="调整搜索 / 部门 / 日期后再试；积分口径与系统设置 KPI 考核规则一致。"
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs text-slate-500">
                  <th className="py-2 pr-3">
                    <button type="button" onClick={() => toggleSort('name')} data-testid="kpi-th-name">
                      员工{sortArrow('name')}
                    </button>
                  </th>
                  <th className="py-2 pr-3">部门 / 岗位</th>
                  <th className="py-2 pr-3 text-right">
                    <button type="button" onClick={() => toggleSort('onlineDurationSec')} data-testid="kpi-th-online">
                      在线时长{sortArrow('onlineDurationSec')}
                    </button>
                  </th>
                  <th className="py-2 pr-3 text-right">
                    <button type="button" onClick={() => toggleSort('onlineTimePoints')}>
                      在线积分{sortArrow('onlineTimePoints')}
                    </button>
                  </th>
                  <th className="py-2 pr-3 text-right">
                    <button type="button" onClick={() => toggleSort('learningPoints')}>
                      学习积分{sortArrow('learningPoints')}
                    </button>
                  </th>
                  <th className="py-2 pr-3 text-right">
                    <button type="button" onClick={() => toggleSort('qcPoints')}>
                      质检积分{sortArrow('qcPoints')}
                    </button>
                  </th>
                  <th className="py-2 pr-3 text-right">
                    <button type="button" onClick={() => toggleSort('totalPoints')} data-testid="kpi-th-total">
                      总积分{sortArrow('totalPoints')}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.employeeId}
                    className="border-b border-slate-100"
                    data-testid={`kpi-row-${r.employeeId}`}
                  >
                    <td className="py-2 pr-3 font-medium text-slate-900">
                      {r.name}
                      {r.username ? (
                        <span className="ml-1 text-[11px] font-normal text-slate-400">@{r.username}</span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3 text-slate-500">
                      {r.deptName ?? '未分配部门'}
                      {r.jobTitle ? ` · ${r.jobTitle}` : ''}
                    </td>
                    <td className="py-2 pr-3 text-right text-slate-600">
                      {formatOnlineDuration(r.onlineDurationSec)}
                    </td>
                    <td className="py-2 pr-3 text-right text-slate-600">{r.onlineTimePoints}</td>
                    <td className="py-2 pr-3 text-right text-slate-600">{r.learningPoints}</td>
                    <td className="py-2 pr-3 text-right text-slate-600">{r.qcPoints}</td>
                    <td className="py-2 pr-3 text-right font-semibold text-slate-900">{r.totalPoints}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
            <span data-testid="kpi-pagination-summary">
              共 {total} 名员工 · 第 {currentPage}/{totalPages} 页
            </span>
            <div className="flex items-center gap-2">
              <select
                className={INPUT}
                value={filter.pageSize}
                onChange={(e) => patch({ pageSize: normalizeKpiPageSize(Number(e.target.value)) })}
                data-testid="kpi-page-size"
              >
                {KPI_PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n} 条/页
                  </option>
                ))}
              </select>
              {total > filter.pageSize ? (
                <>
                  <button
                    type="button"
                    className={SECONDARY_BTN}
                    disabled={currentPage <= 1}
                    onClick={() => setFilter((prev) => ({ ...prev, page: currentPage - 1 }))}
                    data-testid="kpi-prev-page"
                  >
                    上一页
                  </button>
                  <button
                    type="button"
                    className={SECONDARY_BTN}
                    disabled={currentPage >= totalPages}
                    onClick={() => setFilter((prev) => ({ ...prev, page: currentPage + 1 }))}
                    data-testid="kpi-next-page"
                  >
                    下一页
                  </button>
                </>
              ) : null}
            </div>
          </div>
          {/* Issue #386：在线积分防挂机说明——在线时长始终展示，积分可能因当日无学习/质检而为 0。 */}
          <p className="mt-2 text-[11px] leading-relaxed text-slate-400" data-testid="kpi-anti-idle-note">
            在线积分防挂机：若某员工某自然日既无学习积分又无质检积分，该日在线积分记 0（在线时长仍如实展示）。
            规则可在「系统设置 → KPI考核 → 积分规则 → 在线时长」调整。
          </p>
        </>
      )}
    </Card>
  );
}
